import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { getRoomName } from "../data/roomNames";

export type Language = "hu" | "ro" | "en" | "de";
export type RoomId = "residence" | "dining" | "mirror" | "salon" | "estate" | "legacy" | "war" | "extra";
export type RoomTranslation = { id: RoomId; number: number | null; name: string; location: string; tasks: Record<string, string> };
type RoomTranslationJson = Omit<RoomTranslation, "name">;
type TranslationData = { language?: { code: Language; name: string }; rooms?: RoomTranslationJson[]; [key: string]: unknown };
type TranslationParams = Record<string, string | number>;
type LocalizationValue = { language: Language; setLanguage: (language: Language) => void; t: (key: string, params?: TranslationParams) => string; room: (id: RoomId) => RoomTranslation | undefined };

const LocalizationContext = createContext<LocalizationValue | null>(null);
const supportedLanguages: Language[] = ["hu", "ro", "en", "de"];

function initialLanguage(): Language {
  const saved = localStorage.getItem("mariaffi-language") as Language | null;
  if (saved && supportedLanguages.includes(saved)) return saved;
  const browserLanguage = navigator.language.slice(0, 2) as Language;
  return supportedLanguages.includes(browserLanguage) ? browserLanguage : "hu";
}

export function LocalizationProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(initialLanguage);
  const [translations, setTranslations] = useState<TranslationData>({});

  useEffect(() => {
    document.documentElement.lang = language;
    if (typeof fetch !== "function") return;
    let active = true;
    fetch(`/data/${language}/translation.json`)
      .then((response) => {
        if (!response.ok) throw new Error(`Translation request failed: ${response.status}`);
        return response.json();
      })
      .then((data: TranslationData) => active && setTranslations(data))
      .catch(() => active && setTranslations({}));
    return () => { active = false; };
  }, [language]);

  const value = useMemo<LocalizationValue>(() => ({
    language,
    setLanguage: (nextLanguage) => {
      localStorage.setItem("mariaffi-language", nextLanguage);
      setLanguageState(nextLanguage);
    },
    t: (key, params) => {
      const result = key.split(".").reduce<unknown>((current, part) => current && typeof current === "object" ? (current as Record<string, unknown>)[part] : undefined, translations);
      if (typeof result !== "string") return key;
      return Object.entries(params || {}).reduce(
        (text, [name, value]) => text.replaceAll(`{{${name}}}`, String(value)),
        result,
      );
    },
    room: (id) => {
      const translatedRoom = translations.rooms?.find((item) => item.id === id);
      return translatedRoom ? { ...translatedRoom, name: getRoomName(id, language) } : undefined;
    },
  }), [language, translations]);

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
}

export function useTranslation() {
  const context = useContext(LocalizationContext);
  if (!context) throw new Error("useTranslation must be used inside LocalizationProvider");
  return context;
}
